/**
 * Translation STT Manager
 * 
 * Unified interface for speech-to-text during translation sessions.
 * Automatically selects the appropriate STT service based on languages:
 * 
 * - Deepgram Nova-3 multilingual: For English, French, Spanish, etc.
 * - Google Cloud STT: For Swahili and other languages not supported by Deepgram
 * 
 * Key features:
 * - Single connection for entire translation session (no stop/start on speaker changes)
 * - Audio muting during TTS playback (instead of stopping recognition)
 * - Consistent API regardless of backend service
 * - Automatic language detection within session
 */

import { deepgramSTTService } from './deepgramSTTService';
import { googleSTTService } from './googleSTTService';
import { getFunctions, httpsCallable } from 'firebase/functions';

// STT Provider types (canonical definition -- also used by useSymptomLanguage)
export const STT_PROVIDER = {
  DEEPGRAM: 'deepgram',
  GOOGLE: 'google',
  WEB_SPEECH: 'webSpeech', // Browser native (fallback)
};

// Languages that require Google Cloud STT (not supported by Deepgram multilingual)
const GOOGLE_ONLY_LANGUAGES = ['sw'];  // Swahili

// Languages supported by Deepgram multilingual
const DEEPGRAM_MULTILINGUAL_LANGUAGES = ['en', 'es', 'fr', 'de', 'hi', 'ru', 'pt', 'ja', 'it', 'nl'];

/**
 * Translation STT Manager Class
 * 
 * Provides a unified interface for STT during translation sessions.
 */
class TranslationSTTManager {
  constructor() {
    this.activeProvider = null;
    this.activeService = null;
    this.isConnected = false;
    this.isMuted = false;
    this.languages = [];
    
    // Callbacks
    this.onTranscript = null;
    this.onInterimTranscript = null;
    this.onError = null;
    this.onConnectionChange = null;
    
    // Audio buffer for muted periods (optional - for catching speech during unmute)
    this.muteStartTime = null;
    
    // Firebase functions for Google STT (used for batch fallback)
    this.functions = getFunctions();
    this.googleSTTTranscribeFn = httpsCallable(this.functions, 'googleSTTTranscribe');
  }

  /**
   * Determine which STT provider to use based on languages
   * @param {string} providerLanguage - Provider's language code
   * @param {string} patientLanguage - Patient's language code
   * @returns {string} STT_PROVIDER value
   */
  selectProvider(providerLanguage, patientLanguage) {
    const languages = [providerLanguage, patientLanguage];
    
    // Check if any language requires Google Cloud STT
    const needsGoogle = languages.some(lang => {
      const langBase = lang.split('-')[0].toLowerCase();
      return GOOGLE_ONLY_LANGUAGES.includes(langBase);
    });
    
    if (needsGoogle) {
      return STT_PROVIDER.GOOGLE;
    }
    
    // Check if all languages are supported by Deepgram multilingual
    const allSupportedByDeepgram = languages.every(lang => {
      const langBase = lang.split('-')[0].toLowerCase();
      return DEEPGRAM_MULTILINGUAL_LANGUAGES.includes(langBase);
    });
    
    if (allSupportedByDeepgram) {
      return STT_PROVIDER.DEEPGRAM;
    }
    
    // Default to Google for any unsupported language combinations
    return STT_PROVIDER.GOOGLE;
  }

  /**
   * Set callback handlers
   * @param {Object} handlers - { onTranscript, onInterimTranscript, onError, onConnectionChange }
   */
  setHandlers(handlers) {
    if (handlers.onTranscript) this.onTranscript = handlers.onTranscript;
    if (handlers.onInterimTranscript) this.onInterimTranscript = handlers.onInterimTranscript;
    if (handlers.onError) this.onError = handlers.onError;
    if (handlers.onConnectionChange) this.onConnectionChange = handlers.onConnectionChange;
  }

  /**
   * Initialize and connect to the appropriate STT service
   * @param {string} providerLanguage - Provider's language code
   * @param {string} patientLanguage - Patient's language code
   * @returns {Promise<boolean>} Success status
   */
  async connect(providerLanguage, patientLanguage) {
    this.languages = [providerLanguage, patientLanguage];
    this.activeProvider = this.selectProvider(providerLanguage, patientLanguage);
    

    // Set up the appropriate service
    if (this.activeProvider === STT_PROVIDER.DEEPGRAM) {
      this.activeService = deepgramSTTService;
      
      // Configure for multilingual mode; enable diarization for provider/patient attribution
      deepgramSTTService.configure({
        language: 'multi',  // Automatic language detection
        enableCodeSwitching: true,
        diarize: true,      // Speaker labels (0, 1) for segment attribution
      });
      
      // Set up handlers to forward to our callbacks
      deepgramSTTService.setHandlers({
        onTranscript: (result) => {
          if (!this.isMuted) {
            this.onTranscript?.(result);
          }
        },
        onInterimTranscript: (result) => {
          if (!this.isMuted) {
            this.onInterimTranscript?.(result);
          }
        },
        onError: (error) => this.onError?.(error),
        onConnectionChange: (state) => {
          this.isConnected = state === 'connected';
          this.onConnectionChange?.(state);
        },
      });
      
      // Connect to Deepgram
      const success = await deepgramSTTService.connect();
      this.isConnected = success;
      return success;
      
    } else {
      // Google Cloud STT
      this.activeService = googleSTTService;
      
      // Configure for multilingual recognition
      googleSTTService.configure({
        languageCode: providerLanguage,
        alternativeLanguageCodes: [patientLanguage].filter(l => l !== providerLanguage),
      });
      
      // Set up handlers
      googleSTTService.setHandlers({
        onTranscript: (result) => {
          if (!this.isMuted) {
            this.onTranscript?.(result);
          }
        },
        onInterimTranscript: (result) => {
          if (!this.isMuted) {
            this.onInterimTranscript?.(result);
          }
        },
        onError: (error) => this.onError?.(error),
        onConnectionChange: (state) => {
          this.isConnected = state === 'connected';
          this.onConnectionChange?.(state);
        },
      });
      
      // Connect to Google Cloud STT
      const success = await googleSTTService.connect(this.languages);
      this.isConnected = success;
      return success;
    }
  }

  /**
   * Send audio data to the active STT service
   * @param {ArrayBuffer|Uint8Array} audioData - Raw audio data
   * @returns {boolean} Whether audio was sent (false if muted)
   */
  sendAudio(audioData) {
    if (this.isMuted) {
      // Don't send audio during TTS playback
      return false;
    }
    
    if (!this.isConnected || !this.activeService) {
      return false;
    }
    
    return this.activeService.sendAudio(audioData);
  }

  /**
   * Mute audio streaming (called when TTS starts)
   * This prevents the microphone from picking up TTS playback
   */
  muteAudio() {
    this.isMuted = true;
    this.muteStartTime = Date.now();
  }

  /**
   * Unmute audio streaming (called when TTS ends)
   */
  unmuteAudio() {
    this.isMuted = false;
    this.muteStartTime = null;
  }

  /**
   * Check if audio is currently muted
   * @returns {boolean}
   */
  isAudioMuted() {
    return this.isMuted;
  }

  /**
   * Disconnect from the active STT service
   */
  disconnect() {
    
    if (this.activeService) {
      this.activeService.disconnect();
    }
    
    this.isConnected = false;
    this.isMuted = false;
    this.activeProvider = null;
    this.activeService = null;
    this.languages = [];
    this.onConnectionChange?.('disconnected');
  }

  /**
   * Transcribe audio using batch mode (fallback for when streaming isn't available)
   * @param {string} audioBase64 - Base64-encoded audio
   * @param {string} languageCode - Language code
   * @returns {Promise<Object>} Transcription result
   */
  async transcribeBatch(audioBase64, languageCode) {
    
    try {
      const result = await this.googleSTTTranscribeFn({
        audio: audioBase64,
        languageCode,
        alternativeLanguageCodes: this.languages.filter(l => l !== languageCode),
      });
      
      return result.data;
    } catch (error) {
      console.error('[TranslationSTT] Batch transcription error:', error);
      throw error;
    }
  }

  /**
   * Get current status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isMuted: this.isMuted,
      activeProvider: this.activeProvider,
      languages: this.languages,
    };
  }
}

// Export singleton instance
export const translationSTTManager = new TranslationSTTManager();

// Export class for testing
export { TranslationSTTManager };

export default translationSTTManager;
